import { DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { forwardRef, useRef, useState } from 'react';
import { toPartial, type AntdValue, type XmRangeDatePickerProps, type XmRangeDatePickerRef } from './rangePickerAntd';
import { endpointOf, type PartialRangeValue, type RangeEndpoint } from './rangePickerState';
import { useCandidateCells } from './useCandidateCells';
import { useRangeHandle } from './useRangeHandle';

const { RangePicker } = DatePicker;

/** Pair antd reported during this session, tagged with the controlled value it was built on. */
interface StockEdit {
  readonly base: AntdValue;
  readonly current: PartialRangeValue | null;
}

interface StockSession {
  readonly active: RangeEndpoint;
  readonly open: boolean;
  readonly current: PartialRangeValue | null;
  readonly handlers: Pick<RangePickerProps, 'onFocus' | 'onCalendarChange' | 'onChange' | 'onOpenChange'>;
}

/** Mirrors antd's shown pair and active field so the hover candidate has an anchor. */
function useStockSession(props: RangePickerProps): StockSession {
  const controlled = 'value' in props;
  const uncontrolled = useRef<PartialRangeValue | null>(toPartial(props.defaultValue));
  const [edit, setEdit] = useState<StockEdit | null>(null);
  const [active, setActive] = useState<RangeEndpoint>(0);
  const [open, setOpen] = useState<boolean>(props.open ?? props.defaultOpen ?? false);
  const committed = controlled ? toPartial(props.value) : uncontrolled.current;
  // A controlled value swapped under an open panel outdates the pair antd reported for the old one.
  const current = edit && (!controlled || edit.base === props.value) ? edit.current : committed;
  const handlers: StockSession['handlers'] = {
    onFocus: (event, info) => {
      props.onFocus?.(event, info);
      const endpoint = endpointOf(info.range);
      if (endpoint !== null) setActive(endpoint);
    },
    onCalendarChange: (dates, dateStrings, info) => {
      props.onCalendarChange?.(dates, dateStrings, info);
      setEdit({ base: props.value, current: toPartial(dates) });
    },
    onChange: (dates, dateStrings) => {
      props.onChange?.(dates, dateStrings);
      if (!controlled) uncontrolled.current = toPartial(dates);
    },
    onOpenChange: next => {
      props.onOpenChange?.(next);
      setOpen(next);
      setEdit(null);
    },
  };
  return { active, open: props.open ?? open, current, handlers };
}

const StockRangePicker = forwardRef<XmRangeDatePickerRef, XmRangeDatePickerProps>((props, ref) => {
  const picker = useRangeHandle(ref);
  const { active, open, current, handlers } = useStockSession(props);
  const cellRender = useCandidateCells({ current, active, open, props });
  return (
    <div className="xm-range-picker">
      <RangePicker {...props} {...handlers} ref={picker} previewValue={false} cellRender={cellRender} />
    </div>
  );
});

export default StockRangePicker;
